import { StructuredDataService } from "@brookbush/structured-data";
import type {
	Certification,
	CourseAccreditation,
	CourseAccreditationByProfession,
	CourseCategory,
	CourseCategoryShallow,
	CoursePreview,
} from "@brookbush/types";

import type { GetStaticPaths, GetStaticProps } from "next";
import { parseAsString, useQueryState } from "next-usequerystate";
import { useMemo } from "react";

import CoursesCatalogue from "../components/CoursesCatalogue";
import CoursesCategory from "../components/CoursesCategory";
import type { BreadcrumbItem } from "~/components/Breadcrumbs";
import { GridLayout } from "~/layouts";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

interface CoursesCategoryPageProps {
	shallowCategories: CourseCategoryShallow[];
	accreditationsByProfession: CourseAccreditationByProfession[];
	certifications: Certification[];
	category: CourseCategory;
	structuredData: string;
}

const CoursesCategoryPage: NextPageWithProps<CoursesCategoryPageProps> = ({
	shallowCategories,
	accreditationsByProfession,
	certifications,
	structuredData,
}) => {
	const [accreditationFilter] = useQueryState("accreditation", parseAsString);

	const selectedAccreditation = useMemo(
		() =>
			accreditationsByProfession
				.reduce(
					(acc: CourseAccreditation[], curr) =>
						acc.concat(...curr.accreditations),
					[],
				)
				.find((acc) => acc.slug === accreditationFilter),
		[accreditationFilter, accreditationsByProfession],
	);

	return (
		<>
			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>
			<GridLayout.Sidebar className="z-20 laptop:block hidden">
				<CoursesCatalogue
					certifications={certifications}
					shallowCategories={shallowCategories}
					accreditationsByProfession={accreditationsByProfession}
				/>
			</GridLayout.Sidebar>

			<GridLayout.Body>
				<CoursesCategory
					shallowCategories={shallowCategories}
					selectedAccreditation={selectedAccreditation}
					accreditationsByProfession={accreditationsByProfession}
				/>
			</GridLayout.Body>
		</>
	);
};

CoursesCategoryPage.Layout = GridLayout;

export const getStaticPaths: GetStaticPaths = () => {
	return {
		paths: [],
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps<
	any,
	{
		slugs: string[];
	}
> = async ({ params }) => {
	const [
		shallowCategories,
		category,
		accreditationsByProfession,
		certifications,
		totalCredits,
	] = await Promise.all([
		fetcher("/courses/categories/shallow"),
		fetcher(`/courses/categories/${params?.slugs.join("/")}`),
		fetcher("/courses/accreditations/by-profession?filter=true"),
		fetcher(`/certifications`),
		fetcher("/courses/credits/total"),
	]);

	const parentCategory =
		category &&
		shallowCategories.find((cat: CourseCategoryShallow) =>
			cat.subCategories?.find((subCat) => subCat.slug === category.slug),
		);

	const breadcrumbs = [
		{
			href: "/courses",
			name: "Courses",
		},
		{
			href: "/courses",
			name: "Categories",
		},

		parentCategory && {
			name: parentCategory.title,
			href: `/courses/categories/${parentCategory.slug}`,
		},

		{
			name: category.title,
			href: parentCategory
				? `/courses/categories/${parentCategory.slug}/${category.slug}`
				: `/courses/categories/${category.slug}`,
		},
	].filter(Boolean) as BreadcrumbItem[];

	// If category has sub-categories, combine courses from sub-categories
	// Else return category.courses
	const courses: Array<CoursePreview> = category.subCategories
		? category.subCategories.reduce(
				(accumulator: Array<CoursePreview>, category: CourseCategory) => {
					return category.courses
						? accumulator.concat(category.courses)
						: accumulator;
				},
				[],
			)
		: category.courses;

	const structuredData = await Promise.all([
		StructuredDataService.generateCourses(courses),
		StructuredDataService.getBreadcrumbs(
			parentCategory
				? `/courses/categories/${parentCategory.slug}/${category.slug}`
				: `/courses/categories/${category.slug}`,
			(fragment) => (fragment === category.slug ? category.title : undefined),
		),
	]);

	for (const result of structuredData) {
		if (!result) {
			return {
				notFound: true,
			};
		}
	}

	return {
		props: {
			shallowCategories,
			category,
			accreditationsByProfession,
			certifications,
			breadcrumbs,
			structuredData: StructuredDataService.withContext({
				"@graph": structuredData,
			}),
			fallback: {
				[`/certifications`]: certifications,
				["/courses/credits/total"]: totalCredits,
				["/courses/categories/shallow"]: shallowCategories,
				[`/courses/categories/${params?.slugs.join("/")}`]: category,
			},
		},
	};
};

export default CoursesCategoryPage;
